import type { Chain } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import {
  domeLight,
  type Ellipse,
  fromPath,
  fromSegment,
  type HeadPicture,
  inEllipse,
  mask,
  type SetFlair,
  toneOf,
} from './mythic';

/**
 * The Jester set (ADR-0160 amended, mythic): a split porcelain mask under a three-pointed cap with bells, motley, and
 * the Jack-in-the-Box gun. The one mythic with a sense of humour, and still a premium piece.
 *
 * The head is a hood in the team's colour with a porcelain mask over the face (a mask over the whole head, tied on): lit
 * from the top-left, rimmed in gold and split down a gold seam, its near half white and smiling (a red cheek), its far
 * half grey and frowning (a tear of the team's light), the two halves of the mouth not meeting at the seam; black
 * harlequin diamonds over the eyes with the team's light in them; a pleated white ruff under the chin. Out of the hood's
 * crown stand the stiff bases of the cap's three points (the team's colour either side, black in the middle), and its
 * flair is the rest of them: three floppy points (chains of round links, a satin sheen down each) with a gold bell at
 * every end, bobbing on their own when it stands, flopping and streaming back as it runs, the bells glinting. Round it
 * three playing cards circle at the waist; confetti is sprinkled behind it on the run. The motley is diamonds of the
 * team's colour and black, a puffed sleeve of each, gold buttons, a dagged hem with a bell on every point.
 *
 * Rubric (`assetc mythic` at 5× and 8×, 1:1 on grass, lane and night, both teams, idle, run and dead, beside Inferno):
 * mask and cap S5 R5 P4 L4 C4 A4 F4, motley S4 R4 P4 L4 C4 F4. Still weak: a bell is two of the chain's square dots (a
 * gold block more than a bell at 5×); the motley's diamonds are mostly behind the gun (the sleeve and the hem carry
 * it); the hood is a wide field of the team's colour round the mask.
 */

// --- The mask and the cap --------------------------------------------------------------------------------------------

/** From the head's middle (u forward, v down): the hood under the cap, the mask over the face, the ruff under the chin. */
const HOOD: Ellipse = [-1.5, -3, 21.5, 19.5];
/** The hood falling to the nape, over the neck. */
const NAPE: Ellipse = [-13.5, 9.5, 7.5, 6.5];
const FACE: Ellipse = [5, 3.5, 15, 13.5];
const RUFF: Ellipse = [2, 18, 17.5, 4.4];
/** The ruff's pleats, art px apart. */
const PLEAT = 4;
/** A seam down the hood, from the crown to the nape. */
const GORE = [-7, -18.5, -13, -10, -16.5, 0, -17, 10];
/** The mask's seam: the smiling half behind it (the near side), the frowning half in front of it. */
const SEAM = 5.5;
/** The cap's stiff bases: [base u, base v, tip u, tip v, half-width at the base, at the tip, black]. */
const STUBS: readonly (readonly [number, number, number, number, number, number, boolean])[] = [
  [-9, -13, -18, -21.5, 4.6, 2.8, false],
  [0, -16, -0.5, -25, 4.6, 2.8, true],
  [9.5, -13, 17.5, -21, 4.6, 2.8, false],
];
/** The eyes in their black diamonds: [middle u, middle v, half-width, half-height]. */
const EYES: readonly (readonly [number, number, number, number])[] = [
  [-0.5, 1, 3.6, 6.5],
  [11.5, 1, 2.8, 6],
];
/** The mouth, two halves that don't meet at the seam: a smile's turning up the near half, a frown's down the far one. */
const SMILE = [SEAM, 11.8, 2, 11.6, -1.5, 10.6, -4, 8.8, -5.5, 7];
const FROWN = [SEAM, 9.6, 9, 9.9, 12, 11, 14.5, 13];

const PORCELAIN = [
  ['w', 0.1],
  ['5', -0.35],
] as const;
const SHADED = [
  ['5', 0.1],
  ['4', -0.3],
] as const;

const inFace = (u: number, v: number, grow = 0) =>
  inEllipse(u, v, [FACE[0], FACE[1], FACE[2] + grow, FACE[3] + grow]) <= 1;

/**
 * Where (u, v) is on a stub: how far across it (-1 … 1, + on the side facing up into the light: the light from a little
 * higher than (lx, ly), so a stub pointing at it still has a lit side), or null off it.
 */
function onStub(u: number, v: number, [bu, bv, tu, tv, w0, w1]: (typeof STUBS)[number], lx: number, ly: number) {
  const [dx, dy] = [tu - bu, tv - bv];
  const len = Math.hypot(dx, dy);
  const t = ((u - bu) * dx + (v - bv) * dy) / (len * len);
  if (t < -0.12 || t > 1) return null;
  const [nx, ny] = [-dy / len, dx / len];
  const across = (u - bu) * nx + (v - bv) * ny;
  const half = w0 + (w1 - w0) * Math.max(0, t);
  if (Math.abs(across) > half) return null;
  const [c, s] = [Math.cos(0.32), Math.sin(0.32)];
  return (across / half) * Math.sign(nx * (lx * c - ly * s) + ny * (lx * s + ly * c) || 1);
}

/** How far along a stub (u, v) is, 0 at its base, 1 at its tip. */
function onStubAt(u: number, v: number, [bu, bv, tu, tv]: (typeof STUBS)[number]): number {
  const [dx, dy] = [tu - bu, tv - bv];
  return ((u - bu) * dx + (v - bv) * dy) / (dx * dx + dy * dy);
}

const jester: HeadPicture = (u, v, lx, ly) => {
  if (inFace(u, v)) {
    // The mask's gold rim, lit where it faces the light.
    if (!inFace(u, v, -1.3)) return (u - FACE[0]) * lx + (v - FACE[1]) * ly > 0 ? 'y' : 'Y';
    for (const [eu, ev, hw, hh] of EYES) {
      const d = Math.abs(u - eu) / hw + Math.abs(v - ev) / hh;
      if (d >= 1) continue;
      // The eye: the team's light in the diamond's middle.
      const eye = Math.hypot(u - eu, (v - ev - 0.4) * 1.3);
      if (eye < 0.9) return 'w';
      if (eye < 1.9) return 'c';
      if (eye < 2.5) return 'b';
      return 'k';
    }
    if (fromPath(u, v, SMILE) < 0.75 || fromPath(u, v, FROWN) < 0.75) return 'R';
    // A red cheek on the smiling half, a tear of the team's light on the frowning one.
    if (inEllipse(u, v, [-5.2, 4.8, 2.2, 1.6]) <= 1) return u + v < -1 ? 'i' : 'r';
    if (inEllipse(u, v, [14.6, 9.2, 1.1, 1.4]) <= 1 || fromSegment(u, v, 14.6, 8.4, 14.2, 6.6) < 0.5)
      return v < 8.6 ? 'c' : 'b';
    if (Math.abs(u - SEAM) < 0.5) return 'Y';
    const l = domeLight(u, v, FACE, lx, ly);
    return u < SEAM ? toneOf(l, PORCELAIN, '4') : toneOf(l, SHADED, '3');
  }
  // The ruff: white pleats round the neck, scalloped along its edge.
  const pleat = (((u + 40) % PLEAT) + PLEAT) % PLEAT;
  const scallop = v > RUFF[1] ? Math.cos(((pleat / PLEAT) * 2 - 1) * Math.PI) * 0.8 : 0;
  if (inEllipse(u, v, [RUFF[0], RUFF[1], RUFF[2], RUFF[3] - scallop]) <= 1) {
    // Each pleat a fold lit on its left, in shade on its right, a crease between.
    const l = domeLight(u, v, RUFF, lx, ly) + (pleat < 2 ? 0.25 : -0.25);
    if (pleat < 0.9) return '4';
    return toneOf(
      l,
      [
        ['w', 0.2],
        ['5', -0.45],
      ],
      '4',
    );
  }
  for (const stub of STUBS) {
    const across = onStub(u, v, stub, lx, ly);
    if (across === null) continue;
    const black = stub[6];
    if (across > 0.55) return black ? '3' : 'c';
    // Its root sinks into the hood without a crease.
    if (across < -0.35 && onStubAt(u, v, stub) < 0.15) return black ? '2' : 'b';
    if (across < -0.35) return black ? '1' : 'B';
    return black ? '2' : 'b';
  }
  if (inEllipse(u, v, HOOD) > 1 && inEllipse(u, v, NAPE) > 1) return null;
  // The hood, in the team's colour, a seam down it.
  if (fromPath(u, v, GORE) < 0.6) return 'B';
  return toneOf(
    domeLight(u, v, HOOD, lx, ly),
    [
      ['c', 0.96],
      ['b', -0.05],
    ],
    'B',
  );
};

// --- Motley ----------------------------------------------------------------------------------------------------------

/**
 * The motley standing (the frame's x 6-55, y 39-57): diamonds of the team's colour and black, a white ruff under the
 * chin (the head's own ruff covers it but where the run's lean shows it), gold buttons, a dagged hem with a bell at each
 * point.
 */
function motley(): Part {
  const [x0, y0] = [6, 39];
  const c = new PixelCanvas(50, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const diamond = (x: number, y: number, lit: boolean, shade: boolean): PaletteKey => {
    const a = Math.floor((x + 0.5) / 6 + (y + 0.5) / 7);
    const b = Math.floor((x + 0.5) / 6 - (y + 0.5) / 7);
    const team = ((a + b) & 1) === 0;
    if (team) return lit ? 'c' : shade ? 'B' : 'b';
    return lit ? '2' : shade ? 'k' : '1';
  };
  for (let y = 44; y <= 54; y++)
    for (let x = 16; x <= 43; x++) at(x, y, diamond(x, y, x <= 17 || y === 44, x >= 41 || y >= 53));
  // Puffed sleeves: the near one team, the far one black.
  for (const [cx, team] of [
    [12.5, true],
    [47, false],
  ] as const) {
    const e: Ellipse = [cx, 48, 7, 5.5];
    for (let y = 41; y <= 55; y++)
      for (let x = 5; x <= 55; x++) {
        const r = inEllipse(x + 0.5, y + 0.5, e);
        if (r > 1) continue;
        const l = -(x + 0.5 - cx) / 7 - (y + 0.5 - 48) / 5.5;
        // A puff's seams, top to bottom.
        const seam = Math.abs(x + 0.5 - cx) < 0.6 && r < 0.8;
        if (team) at(x, y, seam ? 'B' : l > 0.5 ? 'c' : l > -0.5 ? 'b' : 'B');
        else at(x, y, seam ? 'k' : l > 0.5 ? '3' : l > -0.5 ? '2' : '1');
      }
  }
  // The ruff: white pleats under the chin, scalloped along its foot.
  for (let x = 15; x <= 45; x++) {
    const k = (x - 15) % 4;
    const foot = 47 + (k === 1 || k === 2 ? 1 : 0);
    for (let y = 42; y <= foot; y++) at(x, y, k === 3 ? '4' : x < 22 || y === 42 ? 'w' : x > 40 ? '4' : '5');
  }
  // Gold buttons down the front.
  for (const y of [50, 53]) {
    at(29, y, 'y');
    at(30, y, 'Y');
    at(29, y + 1, 'Y');
    at(30, y + 1, 'U');
  }
  // The dagged hem: points of the team's colour and black, a bell at each.
  for (let x = 16; x <= 43; x++) {
    const k = (x - 16) % 6;
    const team = Math.floor((x - 16) / 6) % 2 === 0;
    const depth = k < 3 ? k : 5 - k;
    for (let y = 55; y <= 55 + depth; y++) at(x, y, team ? (k === 0 ? 'c' : 'b') : k === 0 ? '2' : '1');
    if (k === 2) at(x, 57, 'Y');
  }
  return [c, x0, y0];
}

/** The motley lying (right of the legs, under the head): the hem at the waist (left), diamonds, the ruff at the neck. */
const DEAD_MOTLEY = grid(`
  ....bbB1
  ...bb11w
  cb1111bw
  cbb11bb5
  c1bbbb15
  b11bb115
  b1bYbb1w
  cbb11bbw
  c1bbbb15
  b11bb115
  b1bYbb1w
  cbb11bb5
  c1bbbb15
  b11bb115
  b1bbbb1w
  cbb11bbw
  ....kkkk
  ....kkkk
`);

/** Black slippers under the team's trousers. */
const MOTLEY = (): Outfit => ({ torso: motley(), dead: [DEAD_MOTLEY, 24, 51], legs: { n: '2', N: '1' } });

// --- Toy colours on every other gun ----------------------------------------------------------------------------------

/** The Jack-in-the-Box finish (the default gun is its own, `pollinator_jester`): purple steel, yellow wood, red brass. */
const TOYBOX = {
  '1': 'k',
  '2': 'P',
  '3': 'P',
  '4': 'p',
  '5': 'i',
  N: 'n',
  n: 'U',
  m: 'Y',
  t: 'y',
  e: 'w',
  U: 'R',
  Y: 'r',
  y: 'i',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const GOLD = 0xfeae34;
const YELLOW = 0xfee761;
const WHITE = 0xffffff;
const INK = 0x181425;

/** A point of the cap: flopping from its stub's tip, a bell at its end (`black`: the middle one, in black). */
const point = (x: number, y: number, dir: number, curl: number, sway: number, phase: number, black = false): Chain => ({
  kind: 'chain',
  at: { on: 'head', x, y },
  n: 9,
  seg: 2.6,
  dir,
  curl,
  stiff: 2.2,
  g: 160,
  drag: 0.04,
  sway: [0.24, sway, phase],
  w: [6, 3.5, 6],
  shape: 'dots',
  tones: black
    ? [0x5a6988, 0x3a4466, 0x3a4466, 0x3a4466, 0x3a4466, 0x262b44, 0x262b44, 0x262b44, YELLOW, GOLD]
    : ['teamLight', 'team', 'team', 'team', 'team', 'team', 'teamDark', 'teamDark', YELLOW, GOLD],
  rim: INK,
  lit: WHITE,
  // The bell glints.
  shed: { rate: 2.5, v: [-2, 2, -4, 0], life: [0.12, 0.22], size: [2, 1], tones: [WHITE, YELLOW], add: true, glow: 4 },
  layer: 'back',
});

/** A playing card circling (three rings stacked: the card's top, its foot, its pip). */
const cards = (y: number, size: number, tone: number) =>
  ({
    kind: 'orbit',
    at: { on: 'torso', x: 30, y },
    rx: 32,
    ry: 7,
    tilt: -0.06,
    n: 3,
    period: 6.5,
    size,
    tones: [tone],
  }) as const;

/** Confetti sprinkled on the run, one colour a part. */
const confetti = (tone: number) =>
  ({
    kind: 'emit',
    at: { on: 'torso', x: 30, y: 44 },
    box: [30, 10],
    rate: 0.3,
    run: 0.03,
    v: [-30, 30, -50, -20],
    g: 170,
    drag: 0.3,
    wobble: [1.5, 3],
    life: [0.5, 0.85],
    size: [2, 2],
    tall: 1.5,
    tones: [tone],
    layer: 'front',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    point(11.5, 6.5, -2.1, -0.36, 0.9, 0),
    point(29.5, 3, -1.45, -0.3, 1.1, 0.33, true),
    point(47.5, 7, -1.05, 0.36, 1, 0.66),
  ],
  outfit: [
    cards(46, 5, WHITE),
    cards(48, 5, WHITE),
    cards(47, 3, 0xe43b44),
    confetti(YELLOW),
    confetti(0xf6757a),
    confetti(0x63c74d),
    confetti(WHITE),
    confetti(0xb55088),
  ],
  gun: [
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 58, y: 12 },
      box: [14, 12],
      n: 4,
      period: [0.5, 0.9],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'gun',
    },
    // A puff of confetti off the clown's hat now and then.
    ...[YELLOW, 0xf6757a, 0x63c74d].map(
      (tone) =>
        ({
          kind: 'emit',
          at: { on: 'gun', x: 55, y: 9 },
          box: [4, 2],
          rate: 0.9,
          v: [-14, 6, -34, -18],
          g: 110,
          drag: 0.4,
          wobble: [1, 3],
          life: [0.45, 0.7],
          size: [1, 1],
          tones: [tone],
          layer: 'gun',
        }) as const,
    ),
  ],
};

/** The Jester set (ADR-0160 amended). */
export const JESTER = drawnLater({ head: () => mask(jester), outfit: MOTLEY, gun: TOYBOX, flair: FLAIR });
